import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  TablePagination,
  Tooltip,
  Typography
} from '@material-ui/core';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { getQuoteProPackagesApi, deleteQuoteProPackageApi, getQuoteProProductsApi } from 'api';
import { TextFormattedNumber } from 'components';
import { useStyles } from './styles';


const QuotePackages = ({setSelectedPackage}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [sort, setSort] = useState({sortField: 'createdAt', sortDir: 'desc'});
  const [searchName, setSearchName] = useState('');
  const [currentPage, setCurrentPage] = useState('1');
  const [rowsPerPage, setRowsPerPage] = useState('20');
  const {sortField, sortDir} = sort;
  const isPatientGroupActive = location.pathname.includes('/quoting/group/');

  const {
    quoteProPackagesData,
    customerPushCart,
    allQuoteProducts
  } = useSelector((state: IReduxState) => state.quote);

  useEffect(() => {
    if (!quoteProPackagesData?.quotePackages?.length || (quoteProPackagesData?.quotePackages?.length < parseInt(rowsPerPage, 10))) {
      getQuoteProPackagesApi({
        dispatch,
        name: '',
        sortField,
        sortDir,
        limit: rowsPerPage,
        page: '1'
      });
    }

    if (!allQuoteProducts.length) {
      getQuoteProProductsApi({ dispatch });
    }
  }, []);

  const {
    quotePackages,
    limit,
    totalCount
  } = quoteProPackagesData ||
      {
        quotePackages: [],
        limit: 20,
        totalCount: 0
      };

  const handleRequestSort = (event, sortField) => {
    const isDesc = sort.sortField === sortField && sort.sortDir === 'desc';
    const sortDir = isDesc ? 'asc' : 'desc';
    setSort({sortField, sortDir});
    setCurrentPage('1');
    getQuoteProPackagesApi({
      dispatch,
      name: searchName,
      sortField,
      sortDir,
      limit: rowsPerPage,
      page: '1'
    });
  };

  const handleClickSearch = (name) => {
    getQuoteProPackagesApi({
      dispatch,
      name,
      sortField,
      sortDir,
      limit: rowsPerPage,
      page: '1'
    });
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage + 1);
    getQuoteProPackagesApi({
      dispatch,
      name: searchName,
      sortField,
      sortDir,
      limit: rowsPerPage,
      page: newPage + 1
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage('1');
    getQuoteProPackagesApi({
      dispatch,
      name: searchName,
      sortField,
      sortDir,
      limit: event.target.value,
      page: '1'
    });
  };

  const handleSetSelectedPackage = (quotePackage) => {
    if (!customerPushCart?.selectedItems || !quotePackage) {
      setSelectedPackage(quotePackage);
      return;
    }

    const updatedSelectedProducts = quotePackage.selectedProducts.map((product) => ({ ...product }));

    customerPushCart.selectedItems.forEach((selectedItem) => {
      const existingIndex = updatedSelectedProducts.findIndex((product) => product.sku === selectedItem.sku);

      if (existingIndex === -1) {
        updatedSelectedProducts.push({ ...selectedItem });
      } else {
        updatedSelectedProducts[existingIndex].qty += selectedItem.qty;
      }
    });

    const updatedSelectedPackage = {
      ...quotePackage,
      selectedProducts: updatedSelectedProducts,
      products: { ...quotePackage.products, ...customerPushCart.products }
    };

    if (isPatientGroupActive) {
      setSelectedPackage(quotePackage);
    } else {
      setSelectedPackage(updatedSelectedPackage);
    }
  };

  return (
    <Grid container spacing={2} alignItems="center" className={classes.quoteGroupDotted}>
      <Typography variant="h6" className="mb-2">
        Select Package
      </Typography>
      <div className="flex flex-row w-full">
        <TextField
          fullWidth
          variant="outlined"
          className="mr-2"
          placeholder="Search Packages"
          value={searchName}
          onChange={(event) => setSearchName(event.target.value)}
          onKeyDown={(event) =>  {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleClickSearch(searchName);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {!searchName.length ?
                  <Icon color="primary">search</Icon>
                  :
                  <Icon
                    color="primary"
                    className="cursor-pointer"
                    onClick={() => {
                      setSearchName('');
                      handleClickSearch('');
                    }}
                  >
                    close
                  </Icon>
                }
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          color="primary"
          onClick={() => handleClickSearch(searchName)}
        >
          Search
        </Button>
      </div>
      <div className="pt-4 w-full min-w-500">
        {quotePackages?.length ?
          <>
            <Table className={classes.packagesTableRoot} aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell className="min-w-160">
                    <TableSortLabel
                      active={sort.sortField === 'name'}
                      IconComponent={sort.sortDir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={(event) => handleRequestSort(event, 'name')}
                    >
                      Name of package
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="min-w-120">
                    <TableSortLabel
                      active={sort.sortField === 'price'}
                      IconComponent={sort.sortDir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon}
                      onClick={(event) => handleRequestSort(event, 'price')}
                    >
                      Price
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className="min-w-160" />
                </TableRow>
              </TableHead>
              <TableBody>
                {quotePackages?.map((quotePackage) => {
                  const products = Object.values(quotePackage.products);

                  const someProductsEnabled = products?.some((product) => {
                    const packageProduct = product as IPackageProduct;
                    const isProductEnabled = allQuoteProducts?.find((product) => product?.sku === packageProduct?.product?.sku)?.status == 1;
                    return isProductEnabled && quotePackage.selectedProducts.length > 0;
                  });

                  return (
                    <TableRow key={quotePackage._id}>
                      <TableCell>{quotePackage.name}</TableCell>
                      <TableCell>
                        <TextFormattedNumber
                          value={quotePackage.price}
                          prefix="$"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Tooltip title={!someProductsEnabled ? 'Some items from this package are disabled' : ''}>
                            <span>
                              <Button
                                color="primary"
                                variant="contained"
                                disabled={!someProductsEnabled}
                                onClick={() => handleSetSelectedPackage(quotePackage)}
                              >
                                Select
                              </Button>
                            </span>
                          </Tooltip>
                          <IconButton
                            className="ml-2"
                            title="Delete"
                            onClick={() => deleteQuoteProPackageApi({ dispatch, id: quotePackage._id })}
                          >
                            <Icon className="text-gray">delete</Icon>
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>

            </Table>
            {!!totalCount &&
              <TablePagination
                rowsPerPageOptions={[20, 40, 80, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={limit}
                page={Number(currentPage) - 1}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            }
          </>
          :
          <p className="text-muted w-full text-center">
            No available packages.
          </p>
        }
      </div>
    </Grid>
  );
};

export default QuotePackages;
